import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { featureFlagApiService } from '../services/featureFlagApiService';
import { debugError, debugLog } from '../utils/debug';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

export interface FeatureFlagContextType {
    flags: Record<string, boolean>;
    isLoading: boolean;
    error: Error | null;
    /** Re-runs the fetch. Exposed so a failed load is recoverable without a full page reload. */
    reload: () => void;
}

/** Attempts per load, including the first. */
const MAX_ATTEMPTS = 3;

/** Backoff before attempt 2 and 3. */
const RETRY_DELAYS_MS = [500, 1500];

// ============================================================================
// CONTEXT CREATION
// ============================================================================

export const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export interface FeatureFlagProviderProps {
    children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
    const [flags, setFlags] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadCount, setReloadCount] = useState<number>(0);
    // Guards against a late response from an abandoned load overwriting a newer one.
    const loadIdRef = useRef<number>(0);

    const reload = useCallback(() => setReloadCount((count) => count + 1), []);

    useEffect(() => {
        const loadId = ++loadIdRef.current;
        let cancelled = false;

        const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

        const loadFeatureFlags = async () => {
            setIsLoading(true);
            setError(null);

            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                    const fetchedFlags = await featureFlagApiService.getFeatureFlags();
                    if (cancelled || loadId !== loadIdRef.current) return;
                    setFlags(fetchedFlags);
                    setError(null);
                    setIsLoading(false);
                    debugLog('[FeatureFlagProvider] Feature flags loaded', { flags: fetchedFlags, attempt });
                    return;
                } catch (err) {
                    if (cancelled || loadId !== loadIdRef.current) return;
                    const loadError = err instanceof Error ? err : new Error('Unknown error loading feature flags');
                    debugError(`[FeatureFlagProvider] Error loading feature flags (attempt ${attempt}/${MAX_ATTEMPTS}):`, loadError);

                    if (attempt < MAX_ATTEMPTS) {
                        await delay(RETRY_DELAYS_MS[attempt - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
                        if (cancelled || loadId !== loadIdRef.current) return;
                        continue;
                    }

                    // Every attempt failed. Keep whatever flags we already had (empty on a first
                    // load) and expose the error so callers can tell "all features off" apart from
                    // "we never found out" -- silently reporting every flag as disabled hides Pro
                    // features with no visible cause.
                    setError(loadError);
                    setIsLoading(false);
                }
            }
        };

        loadFeatureFlags();

        return () => {
            cancelled = true;
        };
    }, [reloadCount]);

    return (
        <FeatureFlagContext.Provider value={{ flags, isLoading, error, reload }}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access feature flag context
 * @returns Feature flag context value
 * @throws Error if used outside of FeatureFlagProvider
 */
export function useFeatureFlagContext(): FeatureFlagContextType {
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
    }
    return context;
}
